import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as BagstageLogo } from "../assets/bagstage_logo_new.svg";
import Footer from "../components/homepage/Footer";
import HomePageCard from "../components/homepage/HomePageCard";
import LanguageSelectionTooltip from "../components/homepage/LanguageSelectionTooltip";
import UserProfile from "../components/homepage/UserProfile";
import { cardListItem } from "../components/UI/Cards/CardListItem";
import PageWrapper from "../components/UI/PageWrapper";
import URLService from "../service/urlService";
import UserService from "../service/userService";
import { useAppSelector } from "../store/hooks";
import homePageStyles from "../styles/homePage.module.css";
import {
  BagstageUserDetails,
  KeycloakUserTokenType
} from "../types/user.types";
import { bagstageApplications } from "../utils/commonUtil";
import { ReactComponent as OperatorIcon } from "../assets/icons/bagstage_operator1.svg";
import { ReactComponent as AnalystIcon } from "../assets/icons/bagstage_analyst_1.svg";
import { ReactComponent as QTBIcon } from "../assets/icons/bagstage_qtb.svg";
import { ReactComponent as CustomsIcon } from "../assets/icons/bagstage_customs.svg";
import { ReactComponent as DepartureModule } from "../assets/icons/bagstage_departure_module.svg";
import { ReactComponent as ArrivalModule } from "../assets/icons/bagstage_arrival_module.svg";
import { ReactComponent as TransferModule } from "../assets/icons/bagstage_transfer_module.svg";
import SettingsOptionButton from "../components/homepage/SettingsOptionButton";
import pageWrapperStyles from "../styles/pageWrapper.module.css";
import ClickAndHoverableTooltip from "../components/UI/tooltips/ClickAndHoverableTooltip";
import tooltipStyles from "../styles/tooltip.module.css";
import ProfileChip from "../components/homepage/ProfileChip";

export default function HomePage() {
  const { t } = useTranslation();
  const { isLogin, kcObject } = useAppSelector(
    (state) => state.authenticationSlice
  );
  const [bagstageUser, setBagstageUser] = useState<BagstageUserDetails>();

  const arrivalLinks: cardListItem[] = [
    {
      appRef: bagstageApplications.arrivalOperator,
      applicationTitle: t("operator"),
      redirectUrl: URLService.generateRedirectUrl(
        window._env_.REACT_APP_ARRIVAL_OPERATOR_URL
      ),
      icon: <OperatorIcon />,
      isModuleEnabled: window._env_.REACT_APP_ARRIVAL_OPERATOR_ENABLED
    },
    {
      appRef: bagstageApplications.arrivalOperator,
      applicationTitle: `${t("operator")} (legacy)`,
      redirectUrl: URLService.generateRedirectUrl(
        window._env_.REACT_APP_ARRIVAL_OPERATOR_LEGACY_URL
      ),
      icon: <OperatorIcon />,
      isModuleEnabled: window._env_.REACT_APP_ARRIVAL_OPERATOR_LEGACY_ENABLED,
      isSmallCard: true
    },
    {
      appRef: bagstageApplications.arrivalAnalyst,
      applicationTitle: t("analyst"),
      redirectUrl: URLService.generateRedirectUrl(
        window._env_.REACT_APP_ARRIVAL_ANALYST_URL
      ),
      icon: <AnalystIcon />,
      isModuleEnabled: window._env_.REACT_APP_ARRIVAL_ANALYST_ENABLED
    },
    {
      appRef: bagstageApplications.arrivalCustoms,
      applicationTitle: t("customs"),
      redirectUrl: URLService.generateRedirectUrl(
        window._env_.REACT_APP_ARRIVAL_CUSTOMS_URL
      ),
      icon: <CustomsIcon />,
      isModuleEnabled: window._env_.REACT_APP_ARRIVAL_CUSTOMS_ENABLED
    }
  ];

  const departureLinks: cardListItem[] = [
    {
      appRef: bagstageApplications.departureOperator,
      applicationTitle: t("operator"),
      redirectUrl: URLService.generateRedirectUrl(
        window._env_.REACT_APP_DEPARTURE_OPERATOR_URL
      ),
      icon: <OperatorIcon />,
      isModuleEnabled: window._env_.REACT_APP_DEPARTURE_OPERATOR_ENABLED
    },
    {
      appRef: bagstageApplications.departureAnalyst,
      applicationTitle: t("analyst"),
      redirectUrl: URLService.generateRedirectUrl(
        window._env_.REACT_APP_DEPARTURE_ANALYST_URL +
          "?access_token=" +
          kcObject?.token
      ),
      icon: <AnalystIcon />,
      isModuleEnabled: window._env_.REACT_APP_DEPARTURE_ANALYST_ENABLED
    }
  ];

  const transferLinks: cardListItem[] = [
    {
      appRef: bagstageApplications.transferQtb,
      applicationTitle: t("qtb-monitor"),
      redirectUrl: URLService.generateRedirectUrl(
        window._env_.REACT_APP_TRANSFER_QTB_URL
      ),
      icon: <QTBIcon />,
      isModuleEnabled: window._env_.REACT_APP_TRANSFER_QTB_ENABLED
    }
  ];

  useEffect(() => {
    if (kcObject?.token && isLogin) {
      (async () => {
        const userData: KeycloakUserTokenType =
          (await kcObject?.loadUserInfo()) as KeycloakUserTokenType;

        if (userData) {
          setBagstageUser({
            userInitials: UserService.getUserInitials(userData),
            userName: UserService.getUsername(userData),
            userEmail: userData.preferred_username ?? ""
          });
        }
      })();
    }
  }, [kcObject, isLogin]);

  return (
    <PageWrapper classes={pageWrapperStyles.mainMenuPageWrapper}>
      <header className={homePageStyles.headerSection}>
        <BagstageLogo />
        <div className="flex gap-spacing-2xl items-center">
          <LanguageSelectionTooltip />

          {bagstageUser && (
            <ClickAndHoverableTooltip
              hoverElementWidth="38"
              hoverElement={<ProfileChip bagstageUser={bagstageUser} />}
              position="bottom"
              tooltipModalWidth={218}
              hoveredChildclasses={`${tooltipStyles.tooltipOnHoverContent} ${tooltipStyles.userProfileTooltip}`}
              clickedChildclasses={tooltipStyles.settingTooltipContent}
              hoveredChild={<UserProfile bagstageUser={bagstageUser} />}
              clickedChild={<SettingsOptionButton />}
            />
          )}
        </div>
      </header>

      <section className={homePageStyles.cardSection}>
        <HomePageCard
          icon={<DepartureModule />}
          title={t("departure")}
          cardItems={departureLinks}
        />

        <HomePageCard
          icon={<ArrivalModule />}
          title={t("arrival")}
          cardItems={arrivalLinks}
        />

        <HomePageCard
          icon={<TransferModule />}
          title={t("transfer")}
          cardItems={transferLinks}
        />
      </section>

      <Footer />
    </PageWrapper>
  );
}
